/*
  Shift modular sections · carousel
  Native scroll-snap track; the arrows scroll one slide at a time and disable at the ends.
  Swipe / trackpad / keyboard scrolling work without JS. With a mouse, drag the media to move the
  track (captions stay selectable text); on release it settles on the nearest slide in the drag
  direction (a click after a drag is ignored).
  A mostly vertical wheel / trackpad scroll over the track scrolls the page: browsers hand a
  slightly diagonal trackpad gesture to the track, which cannot scroll vertically, so the page froze.
*/
(function () {
  if (customElements.get('sr-carousel')) return;

  class SRCarousel extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-sr-viewport]');
      this.prev = this.querySelector('[data-sr-prev]');
      this.next = this.querySelector('[data-sr-next]');
      this.status = this.querySelector('[data-sr-status]');
      this.slides = Array.from(this.querySelectorAll('[data-sr-slide]'));
      if (!this.viewport || this.slides.length === 0) return;

      this.onScroll = this.onScroll.bind(this);
      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
      this.viewport.addEventListener('scrollend', () => this.update());
      this.onWheel = this.onWheel.bind(this);
      this.viewport.addEventListener('wheel', this.onWheel, { passive: false });
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.viewport.addEventListener('pointerdown', this.onPointerDown);
      this.viewport.addEventListener('dragstart', (event) => event.preventDefault());
      this.viewport.addEventListener('click', (event) => {
        if (!this.suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
      }, true);
      if (this.prev) this.prev.addEventListener('click', () => this.prev.getAttribute('aria-disabled') !== 'true' && this.go(-1));
      if (this.next) this.next.addEventListener('click', () => this.next.getAttribute('aria-disabled') !== 'true' && this.go(1));
      this.track = this.querySelector('[data-sr-track]');
      // A frame later, not inside the callback: fitReserve resizes the slides this observer
      // watches, and doing that mid-callback throws "ResizeObserver loop" errors (R-005)
      this.resizeObserver = new ResizeObserver(() => {
        window.cancelAnimationFrame(this.fitFrame);
        this.fitFrame = window.requestAnimationFrame(() => {
          this.fitReserve();
          this.update();
        });
      });
      this.resizeObserver.observe(this.viewport);
      if (this.track) this.resizeObserver.observe(this.track);
      this.update();
    }

    disconnectedCallback() {
      if (this.viewport) {
        this.viewport.removeEventListener('scroll', this.onScroll);
        this.viewport.removeEventListener('wheel', this.onWheel);
      }
      if (this.resizeObserver) this.resizeObserver.disconnect();
      window.cancelAnimationFrame(this.fitFrame);
    }

    // Fit to screen: how much sits under the media (caption gap, tallest caption,
    // arrows below). The CSS takes it off the window height to cap the slide
    // width. Ignores changes under 2px so the width settles instead of chasing
    // caption reflow.
    fitReserve() {
      if (!this.track || !this.track.classList.contains('sr-carousel__track--fit')) return;
      const media = this.slides[0].querySelector('.sr-media');
      if (!media) return;
      let bottom = this.viewport.getBoundingClientRect().bottom;
      const controls = this.querySelector('.sr-carousel__controls');
      if (controls) bottom = Math.max(bottom, controls.getBoundingClientRect().bottom);
      const reserve = Math.round(bottom - media.getBoundingClientRect().bottom);
      if (this.reserve !== undefined && Math.abs(reserve - this.reserve) < 2) return;
      this.reserve = reserve;
      this.track.style.setProperty('--sr-fit-reserve', reserve + 'px');
    }

    onWheel(event) {
      if (event.ctrlKey || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      // deltaMode 1 = lines (Firefox mouse wheels), 2 = pages
      var unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? window.innerHeight : 1;
      event.preventDefault();
      window.scrollBy({ top: event.deltaY * unit, behavior: 'instant' });
    }

    // Mouse drag (touch and pens already scroll the track natively)
    onPointerDown(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      // Dragging over a caption selects its text instead of moving the track
      if (event.target.closest('.sr-carousel__caption')) return;
      this.drag = { id: event.pointerId, x: event.clientX, left: this.viewport.scrollLeft, lastX: event.clientX, lastT: event.timeStamp, v: 0, moved: false };
      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);
    }

    onPointerMove(event) {
      const drag = this.drag;
      if (!drag || event.pointerId !== drag.id) return;
      const dx = event.clientX - drag.x;
      if (!drag.moved) {
        if (Math.abs(dx) < 6) return;
        drag.moved = true;
        window.clearTimeout(this.settle);
        this.viewport.classList.add('is-dragging');
        const selection = window.getSelection && window.getSelection();
        if (selection) selection.removeAllRanges();
      }
      const dt = event.timeStamp - drag.lastT;
      if (dt > 0) drag.v = (event.clientX - drag.lastX) / dt;
      drag.lastX = event.clientX;
      drag.lastT = event.timeStamp;
      this.viewport.scrollLeft = drag.left - dx;
    }

    onPointerUp(event) {
      const drag = this.drag;
      if (!drag || event.pointerId !== drag.id) return;
      this.drag = null;
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
      if (!drag.moved) return;

      // The click that follows this mouseup belongs to the drag, not to a button or link
      this.suppressClick = true;
      window.setTimeout(() => { this.suppressClick = false; }, 0);

      const step = this.step();
      const start = Math.round(drag.left / step);
      const distance = (drag.x - event.clientX) / step;
      let index = start;
      // A drag past 12% of a slide, or a quick flick of 20px or more, moves one slide that way
      if (Math.abs(distance) > 0.12 || (Math.abs(drag.x - event.clientX) >= 20 && Math.abs(drag.v) > 0.25)) {
        const direction = distance !== 0 ? Math.sign(distance) : -Math.sign(drag.v);
        index = start + direction * Math.max(1, Math.round(Math.abs(distance)));
      }
      index = Math.min(Math.max(index, 0), this.slides.length - 1);
      this.goTo(index);
      // Snap stays off until the track has settled, or it would jump to the nearest slide first
      this.settle = window.setTimeout(() => this.viewport.classList.remove('is-dragging'), this.reduceMotion() ? 0 : 600);
    }

    step() {
      if (this.slides.length < 2) return this.viewport.clientWidth;
      return this.slides[1].offsetLeft - this.slides[0].offsetLeft;
    }

    current() {
      return Math.round(this.viewport.scrollLeft / this.step());
    }

    // Scroll position of each slide; the last slides can't reach the left edge, so they stop at the end of the track
    target(index) {
      const max = this.viewport.scrollWidth - this.viewport.clientWidth;
      return Math.min(this.slides[index].offsetLeft - this.slides[0].offsetLeft, max);
    }

    // The nearest slide position past the current one in that direction. Rounding the scroll
    // position to a slide skipped one going back from the end, which sits only part of a slide
    // past the one before it.
    go(direction) {
      const left = this.viewport.scrollLeft;
      const indexes = this.slides.map((slide, index) => index);
      const index = direction < 0
        ? indexes.reverse().find((i) => this.target(i) < left - 2)
        : indexes.find((i) => this.target(i) > left + 2);
      if (index !== undefined) this.goTo(index);
    }

    goTo(index) {
      const left = this.target(index);
      const start = this.viewport.scrollLeft;
      this.viewport.scrollTo({ left, behavior: this.reduceMotion() ? 'auto' : 'smooth' });
      // Some browsers drop smooth scrolling on snap containers; jump if nothing moved
      window.clearTimeout(this.fallback);
      this.fallback = window.setTimeout(() => {
        if (Math.abs(this.viewport.scrollLeft - start) < 1 && Math.abs(left - start) >= 1) {
          this.viewport.style.scrollBehavior = 'auto';
          this.viewport.scrollLeft = left;
          this.viewport.style.scrollBehavior = '';
        }
        this.update();
      }, 350);
    }

    reduceMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    onScroll() {
      window.cancelAnimationFrame(this.frame);
      this.frame = window.requestAnimationFrame(() => this.update());
    }

    update() {
      const max = this.viewport.scrollWidth - this.viewport.clientWidth;
      const left = this.viewport.scrollLeft;
      // aria-disabled, not disabled: a disabled button drops keyboard focus to <body>
      if (this.prev) this.prev.setAttribute('aria-disabled', String(left <= 2));
      if (this.next) this.next.setAttribute('aria-disabled', String(left >= max - 2));
      const index = left >= max - 2 ? this.slides.length : Math.min(this.current() + 1, this.slides.length);
      if (this.status && this.status.dataset.index !== String(index)) {
        this.status.dataset.index = String(index);
        this.status.textContent = 'Slide ' + index + ' of ' + this.slides.length;
      }
    }
  }

  customElements.define('sr-carousel', SRCarousel);

  // Theme editor: jump to the slide being edited
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest('[data-sr-slide]');
    const carousel = slide && slide.closest('sr-carousel');
    if (!carousel || !carousel.slides) return;
    carousel.goTo(carousel.slides.indexOf(slide));
  });
})();
